<template>
    <div class="chart-container">
      <Line v-if="chartData" :data="chartData" :options="chartOptions" />
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, watch } from 'vue';
  import { Line } from 'vue-chartjs';
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale
  } from 'chart.js';
  import Papa from 'papaparse';
  import { computeSMA, computeEMA, computeRSI, computeMACD, computeBollingerBands } from './backend/functions/indicator.js';
  import { useI18n } from 'vue-i18n';
  ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);
  
  const props = defineProps({
    tickerA: { type: String, required: true },
    tickerB: { type: String, required: true },
    tickerC: { type: String, required: true },
    indicator: {
      type: String,
      default: 'sma'
    },
    period: {
      type: Number,
      default: 20
    },
    returnType: {
      type: String,
      default: 'daily' // or 'cumulative'
    }
  });
  const { t } = useI18n(); // Initialize the translation function
  const chartData = ref(null);
  const chartOptions = ref({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: {
        display: true,
        text: ''
      }
    },
    scales: {
      x: {
        title: { display: true, text: t('quantPage.Date') },
        ticks: { maxTicksLimit: 10 }
      },
      y: {
        title: { display: true, text: t('quantPage.Value') }
      }
    }
  });
  
  const parseCSV = async (url) => {
    const response = await fetch(url);
    const text = await response.text();
  
    return new Promise((resolve) => {
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const data = [];
          for (const row of results.data) {
            if (row.Date && row.Close) {
              data.push({ date: row.Date, close: parseFloat(row.Close) });
            }
          }
          resolve(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
        }
      });
    });
  };
  
  const applyIndicator = (prices, indicator, period) => {
    switch (indicator.toLowerCase()) {
      case 'sma': return computeSMA(prices, period);
      case 'ema': return computeEMA(prices, period);
      case 'rsi': return computeRSI(prices, period);
  case 'macd': {
    const { macd, signal } = computeMACD(prices);
    return { macd, signal }; // return both series
  }
      case 'bollinger': return computeBollingerBands(prices, period).middleBand;
      default: return prices;
    }
  };
  
  const loadChartData = async () => {

  
    const dataA = await parseCSV(`/${props.tickerA}.csv`);
    const dataB = await parseCSV(`/${props.tickerB}.csv`);
    const dataC = await parseCSV(`/${props.tickerC}.csv`);

    const dates = dataA.map(d => d.date);
    const closeA = dataA.map(d => d.close);
    const closeB = dataB.map(d => d.close);
    const closeC = dataC.map(d => d.close);
  
    const processedA = applyIndicator(closeA, props.indicator, props.period);
    const processedB = applyIndicator(closeB, props.indicator, props.period);
    const processedC = applyIndicator(closeC, props.indicator, props.period);
  
    chartData.value = {
      labels: dates,
      datasets: [
        {
          label: `${props.tickerA} ${props.indicator.toUpperCase()}`,
          data: processedA,
          borderColor: '#000000',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.3
        },
        {
          label: `${props.tickerB} ${props.indicator.toUpperCase()}`,
          data: processedB,
          borderColor: 'green',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.3
        },
        {
          label: `${props.tickerC} ${props.indicator.toUpperCase()}`,
          data: processedC,
          borderColor: 'red',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.3
        }
      ]
    };
  
    chartOptions.value.plugins.title.text = `${props.period}-Day ${props.indicator.toUpperCase()}: ${props.tickerA} vs ${props.tickerB} vs ${props.tickerC}`;
  };
  
  // Watch for changes in props and reload the chart
  watch([() => props.tickerA, () => props.tickerB, () => props.tickerC, () => props.indicator, () => props.period], loadChartData, { immediate: true });
  </script>

<style scoped>
.chart-container {
  width: 100%;
  height: 400px;
  position: relative;
}
</style>